<template>
  <!-- Controls container -->
  <div class="map-buttons-container justify-content-center" :style="containerStyle">
    <!-- Location -->
    <button class="map-btn" aria-label="My location" title="My location" @click="locationButton">
      <span class="material-icons" id="location_icon">my_location</span>
    </button>

    <!-- Zoom out -->
    <button class="map-btn" aria-label="Zoom out" title="Zoom out" @click="zoomOut">
      <span class="material-icons">remove</span>
    </button>

    <!-- Zoom in -->
    <button class="map-btn" aria-label="Zoom in" title="Zoom in" @click="zoomIn">
      <span class="material-icons">add</span>
    </button>

    <!-- Orientation reset -->
    <button class="map-btn" aria-label="Reset bearing/pitch" title="Reset bearing and tilt" @click="orientationButton">
      <svg width="10" height="23" viewBox="0 0 12 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 0L11.1962 9H0.803848L6 0Z" fill="white"/>
        <path opacity="0.4" d="M6 23L0.803849 14L11.1962 14L6 23Z" fill="white"/>
      </svg>
    </button>

    <!-- Fit bounds -->
    <button class="map-btn" aria-label="Fit map to bounds" title="Fit map to bounds" @click="boundsButton">
      <span class="material-icons">crop_free</span>
    </button>

    <!-- 3D Toggle -->
    <button class="map-btn" aria-label="Toggle 3D mode" title="Toggle 3D terrain" @click="toggle3D" :class="{ active: is3DEnabled }">
      <span><strong>3D</strong></span>
    </button>

    <!-- Full screen (opt-in; main /map only) -->
    <button
      v-if="showFullscreen"
      class="map-btn"
      :aria-label="fullscreenPresentation.label"
      :title="fullscreenPresentation.label"
      :class="{ active: isFullscreen }"
      @click="toggleFullscreen"
    >
      <span class="material-icons">{{ fullscreenPresentation.icon }}</span>
    </button>
  </div>
</template>

<script>
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import emitter from '../../utils/bus';
import { ThreeDimensionalMapManager } from '../../utils/models/MapStyles';
import {
  exitMapFullscreen,
  fullscreenButtonPresentation,
  isMapFullscreen,
  shouldExitOnKeydown,
  syncMapFullscreenWithDocument,
  toggleMapFullscreen,
} from '../../utils/helpers/mapFullscreen';

/* Longer than the geolocation timeout plus one retry, so it only fires if the
   control went quiet without reporting success or failure. */
const LOCATION_SPIN_TIMEOUT_MS = 35000;

export default {
  name: 'MapControlsHorizontal',
  props: {
    /** [[minLng, minLat], [maxLng, maxLat]] */
    fitBounds: {
      type: Array,
      default: () => [[-180, -85], [180, 85]],
    },
    fitBoundsPadding: {
      type: Number,
      default: 40,
    },
    /** Optional margin from top in pixels. Default is 12px from CSS */
    marginTop: {
      type: [Number, String],
      default: null,
    },
    /** Show the full-screen toggle. Off by default so the other map apps are unaffected. */
    showFullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      is3DEnabled: ThreeDimensionalMapManager.is3DModeEnabled(),
      locationSpinTimeoutId: null,
      isFullscreen: isMapFullscreen(document),
    };
  },
  computed: {
    fullscreenPresentation() {
      return fullscreenButtonPresentation(this.isFullscreen);
    },
    containerStyle() {
      if (this.marginTop !== null) {
        // Handle both number (assumed px) and string values (e.g., '20px', '2rem')
        const topValue = typeof this.marginTop === 'number'
          ? `${this.marginTop}px`
          : this.marginTop;
        return { top: topValue };
      }
      return {};
    },
  },
  mounted() {
    /* Stop the spin animation as soon as geolocation resolves */
    window.geolocateLocated = () => this.stopLocationSpin();
    window.geolocateError = () => this.stopLocationSpin();

    emitter.on('map:3d-state-changed', this.handle3DStateChange);

    if (this.showFullscreen) {
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('keydown', this.handleFullscreenKeydown);
    }
  },
  beforeUnmount() {
    this.clearLocationSpinTimeout();
    emitter.off('map:3d-state-changed', this.handle3DStateChange);

    if (this.showFullscreen) {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('keydown', this.handleFullscreenKeydown);
      /* Never leave the page stuck in the fixed-position layout if the map unmounts. */
      if (isMapFullscreen(document)) exitMapFullscreen(document);
    }
  },
  methods: {
    zoomIn()            { window.map.zoomIn(); },
    zoomOut()           { window.map.zoomOut(); },
    orientationButton() { window.map.flyTo({ pitch: 0, bearing: 0 }); },
    boundsButton()      { window.map.fitBounds(this.fitBounds, { padding: this.fitBoundsPadding }); },
    clearLocationSpinTimeout() {
      if (this.locationSpinTimeoutId) {
        window.clearTimeout(this.locationSpinTimeoutId);
        this.locationSpinTimeoutId = null;
      }
    },
    stopLocationSpin() {
      this.clearLocationSpinTimeout();
      document.getElementById('location_icon')?.classList.remove('spin');
    },
    locationButton() {
      document.getElementById('location_icon')?.classList.add('spin');
      /* Last-resort stop: nothing should leave the icon spinning forever. */
      this.clearLocationSpinTimeout();
      this.locationSpinTimeoutId = window.setTimeout(
        this.stopLocationSpin,
        LOCATION_SPIN_TIMEOUT_MS,
      );

      const geoLocate = window.geoLocate;
      if (!geoLocate || typeof geoLocate.permissionAndTrigger !== 'function') {
        console.error('Location control is not ready yet.');
        this.stopLocationSpin();
        return;
      }
      geoLocate.permissionAndTrigger();
    },
    async toggle3D() {
      // Check if user is PRO+ before enabling 3D mode
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }

      const nextState = !ThreeDimensionalMapManager.is3DModeEnabled();
      ThreeDimensionalMapManager.set3DMode(nextState);
      this.is3DEnabled = ThreeDimensionalMapManager.is3DModeEnabled();
    },
    handle3DStateChange(enabled) {
      this.is3DEnabled = enabled;
    },
    toggleFullscreen() {
      this.isFullscreen = toggleMapFullscreen(document);
      this.resizeMapSoon();
    },
    /* Browser left fullscreen on its own (Esc / browser UI): keep the layout and button in step. */
    handleFullscreenChange() {
      syncMapFullscreenWithDocument(document);
      this.isFullscreen = isMapFullscreen(document);
      this.resizeMapSoon();
    },
    /* Pseudo-fullscreen (no Fullscreen API, e.g. iPhone Safari) has no browser Esc handling. */
    handleFullscreenKeydown(e) {
      if (!shouldExitOnKeydown(document, e.key)) return;
      exitMapFullscreen(document);
      this.isFullscreen = false;
      this.resizeMapSoon();
    },
    /* Mapbox usually notices the container change itself; this is cheap insurance. */
    resizeMapSoon() {
      window.requestAnimationFrame(() => window.map?.resize?.());
    },
  },
};
</script>
